import IconCheckmark from 'assets/icons/checkmark-action-done.svg';
import cn from 'classnames';
import Link from 'next/link';
import {
  ReactNode,
  forwardRef,
  DetailedHTMLProps,
  ButtonHTMLAttributes,
  MouseEventHandler,
  AnchorHTMLAttributes,
  RefObject,
  ElementType,
} from 'react';
import IconAnimatedSpinner from 'assets/icons/animated-spinner.svg';

export type Layout = 'contained' | 'outline' | 'text';
type CanonicalLayout = 'contained' | 'outline' | 'text';
type ButtonColor = 'red' | 'white';
type VisualState = 'default' | 'disabled' | 'actionDone';

export type ButtonProps = (
  | DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>
  | DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>
) & {
  actionDone?: boolean;
  color?: ButtonColor;
  as?: string;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
  focus?: boolean;
  href?: string;
  iconLeft?: boolean;
  iconRight?: boolean;
  layout?: Layout;
  loading?: boolean;
  size?: 'xs' | 'base' | 'large';
  styleDisabled?: boolean;
  target?: '_blank' | '_top' | '_self';
  type?: 'button' | 'submit' | 'reset';
  onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
};

interface VariantClasses {
  default: string;
  disabled?: string;
  actionDone?: string;
  focus?: string;
}

// TODO: this is matrix approach is hard to read and it exist only for backward compatibility
const VARIANT_CLASS_NAMES: Record<ButtonColor, Record<CanonicalLayout, VariantClasses>> = {
  red: {
    contained: {
      default:
        'bg-secondary-main text-secondary-contrast lg:hover:bg-secondary-contained-hover-background',
      disabled:
        'bg-action-disabled-background text-action-disabled lg:bg-action-disabled-background lg:hover:text-text-action-disabled',
      actionDone: 'bg-success-main text-success-contrast lg:hover:bg-success-main',
      focus: 'border-[3px] border-error-outlined-resting-border',
    },
    outline: {
      default:
        'border border-secondary-outlined-resting-border text-secondary-main lg:hover:bg-secondary-outlined-hover-background lg:hover:border-secondary-outlined-resting-border lg:hover:text-secondary-main',
      disabled: 'border border-action-disabled-background text-action-disabled',
      actionDone:
        'border border-success-outlined-resting-border text-success-main lg:hover:bg-transparent lg:hover:text-success-main',
      focus: 'border-2',
    },
    text: {
      default: 'text-secondary-main lg:hover:text-secondary-contained-hover-background',
      disabled: 'text-action-disabled',
      actionDone: 'text-success-main',
      focus: '',
    },
  },
  white: {
    contained: {
      default: 'bg-other-white text-primary-dark lg:hover:bg-other-black/5',
      disabled: 'bg-action-disabled-background text-action-disabled',
      actionDone: 'bg-success-main text-success-contrast lg:hover:bg-success-main',
      focus: 'border-[3px] border-other-white/60',
    },
    outline: {
      default:
        'border border-other-white/40 text-text-dark-bg-contrast-other-white lg:hover:bg-other-black/5',
      disabled: 'border border-other-white/20 text-other-white/40',
      actionDone:
        'border border-success-outlined-resting-border text-success-main lg:hover:bg-transparent lg:hover:text-success-main',
      focus: 'border-2 border-other-white/60',
    },
    text: {
      default: 'text-primary-dark lg:hover:text-other-white/80',
      disabled: 'text-other-white/40',
      actionDone: 'text-success-main',
      focus: '',
    },
  },
};

const LAYOUT_BASE_CLASS_NAMES: Record<CanonicalLayout, string> = {
  contained: 'rounded-full',
  outline: 'rounded-full border',
  text: 'rounded-none border-transparent p-0',
};

const getVisualState = ({
  actionDone,
  styleDisabled,
}: {
  actionDone?: boolean;
  styleDisabled?: boolean;
}): VisualState => {
  if (actionDone) return 'actionDone';
  if (styleDisabled) return 'disabled';
  return 'default';
};

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((props, ref) => {
  const {
    actionDone,
    as,
    children,
    className,
    disabled,
    focus,
    href,
    iconLeft,
    iconRight,
    layout = 'contained',
    loading,
    size = 'base',
    styleDisabled,
    target = '_self',
    type,
    onClick,
    color = 'red',
    ...rest
  } = props;

  const icon = actionDone && (
    <IconCheckmark
      className={cn(
        'h-auto',
        size === 'xs' && 'w-3',
        size === 'base' && 'w-3.5',
        size === 'large' && 'w-4'
      )}
    />
  );

  const resolvedColor = color ?? 'red';
  const visualState = getVisualState({ actionDone, styleDisabled });
  const variantClasses = VARIANT_CLASS_NAMES[resolvedColor][layout];

  const buttonClassName = cn(
    'focus-default inline-flex items-center justify-center leading-none text-sm transition-colors duration-700 ease-in-out w-full font-medium cursor-pointer disabled:cursor-default',
    layout === 'text' ? 'p-0' : 'px-3 py-2',
    LAYOUT_BASE_CLASS_NAMES[layout],
    variantClasses[visualState],
    focus && variantClasses.focus,
    size === 'xs' && 'text-xs h-[30px] leading-[18px]',
    size === 'base' && 'h-9 text-sm leading-[21px]',
    size === 'large' && 'h-[42px] text-base leading-normal',
    className
  );

  // Return a link element when href is set
  if (href) {
    return (
      <Link
        as={as}
        href={href}
        passHref
        onClick={onClick}
        className={buttonClassName}
        target={target}
        {...(rest as AnchorHTMLAttributes<HTMLAnchorElement>)}
        ref={ref as RefObject<HTMLAnchorElement>}
      >
        <span className="block">{children}</span>
      </Link>
    );
  }

  const Component = (as || 'button') as ElementType;
  return (
    <Component
      className={buttonClassName}
      disabled={disabled}
      onClick={onClick}
      type={type}
      {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
      ref={ref as RefObject<HTMLButtonElement>}
    >
      {loading ? (
        <IconAnimatedSpinner className="h-auto w-5" data-test="button-loading-spinner" />
      ) : (
        <div className="flex items-center justify-end gap-2 leading-none">
          {iconLeft && icon}
          {/* font size should be changed accordingly to button size*/}
          {children}
          {iconRight && icon}
        </div>
      )}
    </Component>
  );
});

Button.displayName = 'Button';

export default Button;
